import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Textarea } from '../components/ui';

// Promise-based reason-prompt dialog, same shape as ConfirmContext's
// confirm(): `const reason = await prompt('Delete this?'); if (reason === null) return;`
// Used for actions that require the admin to type a justification before a
// destructive/sensitive action goes through (see audit log accountability
// work) — resolves to the trimmed string, or null if cancelled/left empty.
const PromptContext = createContext(null);

export function PromptProvider({ children }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState(null);
  const [value, setValue] = useState('');
  const resolveRef = useRef(null);

  const promptReason = useCallback((msg) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setValue('');
      setMessage(msg);
    });
  }, []);

  const settle = (result) => {
    setMessage(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  };

  const trimmed = value.trim();

  return (
    <PromptContext.Provider value={promptReason}>
      {children}
      <Modal
        open={message !== null}
        onClose={() => settle(null)}
        title={t('Please explain why')}
        size="sm"
        zIndexClass="z-[70]"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(null)}>{t('Cancel')}</Button>
            <Button variant="danger" disabled={!trimmed} onClick={() => settle(trimmed)}>{t('Confirm')}</Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 mb-3">{message}</p>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t('Reason (required)...')}
          rows={3}
          autoFocus
        />
      </Modal>
    </PromptContext.Provider>
  );
}

export function usePrompt() {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    throw new Error('usePrompt must be used within PromptProvider');
  }
  return ctx;
}
